<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.supporter.hero.title')"
      :paragraph="t('jc.view.supporter.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.supporter.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.supporter.hero.float.alt'),
      }"
      :label="t('jc.view.supporter.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.supporter.ourOffer.title')"
      :paragraph="t('jc.view.supporter.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.supporter.ourConditions.title')"
      :paragraph="t('jc.view.supporter.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.supporter.yourTasks.title')"
      :paragraph="t('jc.view.supporter.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.supporter.impressions.title')"
      :paragraph="t('jc.view.supporter.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.supporter.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.supporter.howToApply.title')"
      :paragraph="t('jc.view.supporter.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.supporter.moreInformation.title')"
        :paragraph="t('jc.view.supporter.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/supporter.png'

definePageMeta({
  nameKey: 'participate-supporter',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.supporter.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'user-gear',
    text: t('jc.view.supporter.ourOffer.iconHighlight.moderationSystem.text'),
  },
  {
    icon: 'screen-users',
    text: t('jc.view.supporter.ourOffer.iconHighlight.bigCommunity.text'),
  },
  {
    icon: 'arrows-left-right-to-line',
    text: t('jc.view.supporter.ourOffer.iconHighlight.strictJurisdiction.text'),
  },
  {
    icon: 'notebook',
    text: t('jc.view.supporter.ourOffer.iconHighlight.issueTracker.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.supporter.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.supporter.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.supporter.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.supporter.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.supporter.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.supporter.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'gears',
    text: t('jc.view.supporter.ourOffer.icon.supportedWorkflows.text'),
  },
  {
    icon: 'arrow-trend-up',
    text: t('jc.view.supporter.ourOffer.icon.moreResponsibility.text'),
  },
  {
    icon: 'brain-circuit',
    text: t('jc.view.supporter.ourOffer.icon.newTechniques.text'),
  },
  {
    icon: 'handshake',
    text: t('jc.view.supporter.ourOffer.icon.closeCooperation.text'),
  },
  {
    icon: 'comments',
    text: t('jc.view.supporter.ourOffer.icon.exchange.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.supporter.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'scale-balanced',
    text: t('jc.view.supporter.ourConditions.icon.balance.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.supporter.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.supporter.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.supporter.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.supporter.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'hands-holding-child',
    text: t('jc.view.supporter.yourTasks.icon.care.text'),
  },
  {
    icon: 'hands-holding-heart',
    text: t('jc.view.supporter.yourTasks.icon.befriend.text'),
  },
  {
    icon: 'handshake',
    text: t('jc.view.supporter.yourTasks.icon.convey.text'),
  },
  {
    icon: 'party-horn',
    text: t('jc.view.supporter.yourTasks.icon.help.text'),
  },
  {
    icon: 'gavel',
    text: t('jc.view.supporter.yourTasks.icon.moderate.text'),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.supporter.yourTasks.icon.guide.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'tutorial',
    mediaType: 'image/*',
    src: 'images/impressions/supporter/tutorial_menu.png',
  },
  {
    identifier: 'moderation',
    mediaType: 'image/*',
    src: 'images/impressions/supporter/moderation_menu.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.supporter.howToApply.icon.firstContact.title'),
    text: t('jc.view.supporter.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.supporter.howToApply.icon.conversation.title'),
    text: t('jc.view.supporter.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.supporter.howToApply.icon.wait.title'),
    text: t('jc.view.supporter.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.supporter.howToApply.icon.trainee.title'),
    text: t('jc.view.supporter.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.supporter.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.supporter.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.supporter.moreInformation.card.team.text'),
    label: t('jc.view.supporter.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.supporter.moreInformation.card.weekly_review.text'),
    label: t('jc.view.supporter.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.supporter.moreInformation.card.plan.text'),
    label: t('jc.view.supporter.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.supporter.moreInformation.card.stackShare.text'),
    label: t('jc.view.supporter.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.supporter.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.supporter.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.supporter.moreInformation.card.blog.text'),
    label: t('jc.view.supporter.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
